import { Component } from '@theme/component';
import { ThemeEvents, SellingPlanUpdateEvent } from '@theme/events';

/** @extends {Component} */
class FrequencySelectorComponent extends Component {
  #sellingPlanGroups = [];
  #currentAllocations = [];
  #currentVariantId = '';
  #moneyFormat = '${{amount}}';
  #abortController = new AbortController();
  #addToCartTextEl = null;
  #addToCartButton = null;
  #addToCartLabel = '';
  #debugPrefix = '[FrequencySelector]';

  connectedCallback() {
    super.connectedCallback();

    this.#currentVariantId = this.dataset.variantId || '';
    this.#moneyFormat = this.dataset.moneyFormat || '${{amount}}';
    this.#addToCartLabel = this.dataset.addLabel || 'Add To Cart';

    this.#cacheAddToCartButton();
    this.#loadSellingPlanData();
    this.#updatePricesForVariant();
    this.#syncSelectionState();
    this.#fetchRechargeData();

    const section = this.closest('.shopify-section, dialog');

    if (section) {
      section.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, {
        signal: this.#abortController.signal,
      });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  #cacheAddToCartButton() {
    const form = this.closest('form') || this.closest('product-form-component');
    if (!form) return;

    this.#addToCartButton =
      form.querySelector('[ref="addToCartButton"]') ||
      form.querySelector('.add-to-cart-button, button[name="add"]');

    if (this.#addToCartButton) {
      this.#addToCartTextEl =
        this.#addToCartButton.querySelector('.add-to-cart-text__content span span') ||
        this.#addToCartButton.querySelector('.add-to-cart-text__content span') ||
        this.#addToCartButton.querySelector('.add-to-cart-text__content');
    }
  }

  #loadSellingPlanData() {
    const groupsScript = this.querySelector('[data-selling-plan-groups]');
    const allocationsScript = this.querySelector('[data-selling-plan-allocations]');

    try {
      if (groupsScript?.textContent) {
        this.#sellingPlanGroups = JSON.parse(groupsScript.textContent) || [];
      }

      if (allocationsScript?.textContent) {
        this.#currentAllocations = JSON.parse(allocationsScript.textContent) || [];
      }
    } catch (error) {
      console.error('FrequencySelector: Failed to parse selling plan data', error);
    }
  }

  async #fetchRechargeData() {
    const productId = this.dataset.productId;
    if (!productId || typeof recharge === 'undefined') return;

    try {
      const data = await recharge.cdn.getCDNProductAndSettings(Number(productId));

      if (data?.product) {
        this.dataset.rechargeLoaded = 'true';
      }
    } catch (error) {
      console.warn('FrequencySelector: ReCharge CDN fetch failed, using Liquid data', error);
    }
  }

  #getFirstSellingPlan() {
    if (!this.#sellingPlanGroups.length) return { plan: null, allocation: null };

    const plan = this.#sellingPlanGroups[0]?.selling_plans?.[0] || null;

    if (!plan) return { plan: null, allocation: null };

    const allocation = this.#currentAllocations.find(
      (a) => a.selling_plan?.id === plan.id
    ) || null;

    return { plan, allocation };
  }

  #updatePricesForVariant() {
    const { allocation } = this.#getFirstSellingPlan();
    if (!allocation) return;

    const oneTimePriceEl = this.refs.oneTimePrice;
    const subscribePriceEl = this.refs.subscribePrice;
    const subscribePriceStrikeEl = this.refs.subscribePriceStrike;

    const oneTimePrice = Number(this.dataset.variantPrice) || 0;
    const subscribePrice = allocation.per_delivery_price ?? allocation.price;

    if (oneTimePriceEl) {
      oneTimePriceEl.textContent = this.#formatMoney(oneTimePrice);
    }

    if (subscribePriceStrikeEl) {
      subscribePriceStrikeEl.textContent = this.#formatMoney(oneTimePrice);
    }

    if (subscribePriceEl) {
      subscribePriceEl.textContent = this.#formatMoney(subscribePrice);
    }
  }

  #syncSelectionState() {
    this.#updatePurchaseOptionVisibility();

    const checkedRadio = this.querySelector('[data-frequency-input]:checked');
    if (!checkedRadio) return;

    this.#handleFrequencyChange(/** @type {HTMLInputElement} */ (checkedRadio).value);
  }

  #setSubscribePanelState(isOpen) {
    this.dataset.subscribeSelected = isOpen ? 'true' : 'false';

    const panel = this.querySelector('[data-subscribe-panel]');
    if (!panel) return;

    panel.setAttribute('aria-hidden', isOpen ? 'false' : 'true');
  }

  #getSelectedPlanId() {
    const select = this.#getIntervalSelect();
    return select?.value || '';
  }

  #getIntervalSelect() {
    return /** @type {HTMLSelectElement | null} */ (
      this.refs.intervalSelect || this.querySelector('[ref="intervalSelect"]')
    );
  }

  #selectFirstIntervalOption() {
    const select = this.#getIntervalSelect();
    if (!select || !select.options.length) return '';

    const firstOption = select.options[0];
    if (!firstOption) return '';

    if (select.value !== firstOption.value) {
      select.value = firstOption.value;
    }

    console.log(`${this.#debugPrefix} auto-selected first interval option`, {
      value: firstOption.value,
      label: firstOption.textContent,
    });

    return firstOption.value;
  }

  #getAllocationByPlanId(planId) {
    if (!planId) return null;
    const planIdNumber = Number(planId);
    if (!planIdNumber) return null;

    return this.#currentAllocations.find((allocation) => allocation.selling_plan?.id === planIdNumber) || null;
  }

  #getDefaultSubscribePrice() {
    const defaultSubscribePrice = Number(this.dataset.subscribePrice);
    if (defaultSubscribePrice > 0) return defaultSubscribePrice;

    return Number(this.dataset.variantPrice) || 0;
  }

  #isSubscriptionOnly() {
    return this.dataset.requiresSellingPlan === 'true';
  }

  #updatePurchaseOptionVisibility() {
    const oneTimeOption = this.querySelector('[data-one-time-option]');
    const oneTimeInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('[data-one-time-input]')
    );
    const subscribeInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('[data-subscribe-input]')
    );
    const isSubscriptionOnly = this.#isSubscriptionOnly();

    if (oneTimeOption) {
      if (isSubscriptionOnly) {
        oneTimeOption.classList.add('frequency-selector__option--hidden');
      } else {
        oneTimeOption.classList.remove('frequency-selector__option--hidden');
      }
    }

    if (oneTimeInput) {
      oneTimeInput.disabled = isSubscriptionOnly;
      if (isSubscriptionOnly) {
        oneTimeInput.checked = false;
      }
    }

    if (subscribeInput && isSubscriptionOnly) {
      subscribeInput.checked = true;
    }

    console.log(`${this.#debugPrefix} purchase option visibility`, {
      isSubscriptionOnly,
      oneTimeHidden: oneTimeOption?.classList.contains('frequency-selector__option--hidden') || false,
      oneTimeDisabled: oneTimeInput?.disabled || false,
      subscribeChecked: subscribeInput?.checked || false,
    });
  }

  #handleFrequencyChange(value) {
    const { sellingPlanInput } = this.refs;
    if (!sellingPlanInput) return;

    if (value === 'one_time' && this.#isSubscriptionOnly()) {
      const subscribeInput = /** @type {HTMLInputElement | null} */ (
        this.querySelector('[data-subscribe-input]')
      );

      if (subscribeInput) {
        subscribeInput.checked = true;
      }

      value = 'subscribe';
    }

    const oneTimePrice = Number(this.dataset.variantPrice) || 0;
    const hasIntervals = this.dataset.hasIntervals === 'true';

    if (value === 'subscribe') {
      this.#setSubscribePanelState(true);

      let selectedPlanId = '';

      if (hasIntervals) {
        selectedPlanId = this.#selectFirstIntervalOption();
        if (!selectedPlanId) {
          const { plan } = this.#getFirstSellingPlan();
          selectedPlanId = plan?.id?.toString() || '';
        }
      } else {
        selectedPlanId = this.#getSelectedPlanId();
      }

      sellingPlanInput.value = selectedPlanId;
      sellingPlanInput.disabled = !hasIntervals || selectedPlanId === '';

      const selectedAllocation = this.#getAllocationByPlanId(selectedPlanId);
      const subscribePriceFromData = this.#getDefaultSubscribePrice();
      const subscribePrice = selectedAllocation?.per_delivery_price ?? selectedAllocation?.price ?? subscribePriceFromData;
      console.log(`${this.#debugPrefix} subscribe selected`, {
        hasIntervals,
        selectedPlanId,
        selectedAllocation,
        oneTimePrice,
        subscribePriceFromData,
        subscribePrice,
        sellingPlanInputValue: sellingPlanInput.value,
        sellingPlanInputDisabled: sellingPlanInput.disabled,
      });
      this.#updateAddToCartButton(subscribePrice, oneTimePrice);
      this.#dispatchSellingPlanUpdate(subscribePrice, oneTimePrice, selectedPlanId, 'subscribe');
    } else {
      this.#setSubscribePanelState(false);
      sellingPlanInput.value = '';
      sellingPlanInput.disabled = true;

      console.log(`${this.#debugPrefix} one-time selected`, {
        oneTimePrice,
        sellingPlanInputValue: sellingPlanInput.value,
        sellingPlanInputDisabled: sellingPlanInput.disabled,
      });
      this.#updateAddToCartButton(oneTimePrice);
      this.#dispatchSellingPlanUpdate(oneTimePrice, null, '', 'one_time');
    }
  }

  #updateAddToCartButton(priceCents, comparePriceCents) {
    if (!this.#addToCartTextEl) {
      this.#cacheAddToCartButton();
    }
    if (!this.#addToCartTextEl) {
      console.log(`${this.#debugPrefix} ATC text element not found`);
      return;
    }

    const formattedPrice = this.#formatMoney(priceCents);

    if (comparePriceCents && comparePriceCents > priceCents) {
      const formattedCompare = this.#formatMoney(comparePriceCents);
      this.#addToCartTextEl.innerHTML =
        `${this.#addToCartLabel} \u2013 <span class="add-to-cart-price-compare">${formattedCompare}</span> ${formattedPrice}`;
    } else {
      this.#addToCartTextEl.textContent = `${this.#addToCartLabel} \u2013 ${formattedPrice}`;
    }

    if (this.#addToCartButton) {
      const productTitle = this.#addToCartButton.getAttribute('aria-label')?.split(' \u2013 ').pop() || '';
      this.#addToCartButton.setAttribute(
        'aria-label',
        `${this.#addToCartLabel} \u2013 ${formattedPrice}${productTitle ? ` \u2013 ${productTitle}` : ''}`
      );
    }

    console.log(`${this.#debugPrefix} updated ATC button`, {
      priceCents,
      comparePriceCents,
      formattedPrice,
      buttonText: this.#addToCartTextEl.textContent,
      sellingPlanInputValue: this.refs.sellingPlanInput?.value || '',
    });
  }

  handleFrequencySelect(event) {
    const input = /** @type {HTMLInputElement} */ (event.target);
    this.#handleFrequencyChange(input.value);
  }

  handleIntervalChange(event) {
    const select = /** @type {HTMLSelectElement} */ (event.target);
    const selectedPlanId = select.value;
    const selectedFrequency = /** @type {HTMLInputElement | null} */ (
      this.querySelector('[data-frequency-input][value="subscribe"]')
    );

    if (!selectedFrequency?.checked) return;

    const { sellingPlanInput } = this.refs;
    if (!sellingPlanInput) return;

    sellingPlanInput.value = selectedPlanId;
    sellingPlanInput.disabled = selectedPlanId === '';

    const oneTimePrice = Number(this.dataset.variantPrice) || 0;
    const selectedAllocation = this.#getAllocationByPlanId(selectedPlanId);
    const subscribePriceFromData = this.#getDefaultSubscribePrice();
    const subscribePrice = selectedAllocation?.per_delivery_price ?? selectedAllocation?.price ?? subscribePriceFromData;
    console.log(`${this.#debugPrefix} interval changed`, {
      selectedPlanId,
      selectedAllocation,
      oneTimePrice,
      subscribePriceFromData,
      subscribePrice,
      sellingPlanInputValue: sellingPlanInput.value,
      sellingPlanInputDisabled: sellingPlanInput.disabled,
    });
    this.#updateAddToCartButton(subscribePrice, oneTimePrice);
    this.#dispatchSellingPlanUpdate(subscribePrice, oneTimePrice, selectedPlanId, 'subscribe');
  }

  #onVariantUpdate = (event) => {
    const detail = /** @type {any} */ (event).detail;
    if (!detail?.resource) return;

    const newVariantId = detail.resource.id?.toString();
    if (!newVariantId || newVariantId === this.#currentVariantId) return;

    this.#currentVariantId = newVariantId;
    this.dataset.variantId = newVariantId;
    this.dataset.variantPrice = detail.resource.price?.toString() || '0';
    this.dataset.requiresSellingPlan = detail.resource.requires_selling_plan ? 'true' : 'false';

    const newHtml = detail.data?.html;

    if (newHtml) {
      const newComponent = newHtml.querySelector('frequency-selector-component');
      const newAllocationsScript = newComponent?.querySelector('[data-selling-plan-allocations]');
      const newIntervalSelect = /** @type {HTMLSelectElement | null} */ (newComponent?.querySelector('[ref="intervalSelect"]'));
      const currentIntervalSelect = /** @type {HTMLSelectElement | null} */ (this.querySelector('[ref="intervalSelect"]'));

      if (newComponent?.dataset.subscribePrice) {
        this.dataset.subscribePrice = newComponent.dataset.subscribePrice;
      }

      if (newComponent?.dataset.requiresSellingPlan) {
        this.dataset.requiresSellingPlan = newComponent.dataset.requiresSellingPlan;
      }

      if (newAllocationsScript?.textContent) {
        try {
          this.#currentAllocations = JSON.parse(newAllocationsScript.textContent) || [];
        } catch (error) {
          console.error('FrequencySelector: Failed to parse updated allocations', error);
        }
      }

      if (newIntervalSelect && currentIntervalSelect) {
        const previousPlanId = currentIntervalSelect.value;
        currentIntervalSelect.innerHTML = newIntervalSelect.innerHTML;

        if (previousPlanId) {
          const matchingOption = currentIntervalSelect.querySelector(`option[value="${previousPlanId}"]`);
          if (matchingOption) {
            currentIntervalSelect.value = previousPlanId;
          }
        }
      }
    }

    this.#cacheAddToCartButton();
    this.#updatePricesForVariant();
    this.#syncSelectionState();
  };

  #formatMoney(cents) {
    const amount = (cents / 100).toFixed(2);

    return this.#moneyFormat
      .replace('{{amount_with_comma_separator}}', amount.replace('.', ','))
      .replace('{{amount_no_decimals}}', Math.round(cents / 100).toString())
      .replace('{{amount_no_decimals_with_comma_separator}}', Math.round(cents / 100).toString())
      .replace('{{amount_no_decimals_with_space_separator}}', Math.round(cents / 100).toString())
      .replace('{{amount_with_apostrophe_separator}}', amount)
      .replace('{{amount}}', amount);
  }

  #dispatchSellingPlanUpdate(price, comparePrice, sellingPlanId, type) {
    const productId = this.dataset.productId;
    if (!productId) return;

    this.dispatchEvent(new SellingPlanUpdateEvent({
      productId,
      price,
      comparePrice,
      sellingPlanId,
      type,
    }));
  }
}

if (!customElements.get('frequency-selector-component')) {
  customElements.define('frequency-selector-component', FrequencySelectorComponent);
}
